import { TableCell, TableRow } from '@/components/ui/table';
import { cn, formatCurrency } from '@/lib/utils';
import { AGING_BUCKETS, getBucketForInvoice, type AgingBucket } from '../utils/agingBucket';
import type { CustomerInvoice } from '../api';
import type { TriageRow } from '../types/triage';

interface InvoiceRowProps {
  invoice: CustomerInvoice;
  asOfDate: string;
  onClick: (invoice: CustomerInvoice) => void;
  triage?: TriageRow;
}

const BUCKET_CLASSNAMES: Record<AgingBucket, string> = {
  current: '',
  days1to30: '',
  days31to60: 'text-warning',
  days61to90: 'text-orange-600', // TODO GAP-FE-010: no orange semantic token
  days91plus: 'text-destructive',
};

const ACTION_BADGE: Record<string, { label: string; cls: string; title: string }> = {
  apply_payment:   { label: 'apply',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', title: 'Bank deposit matched — apply payment' },
  await_deposit:   { label: 'pending',  cls: 'bg-sky-100 text-sky-800 border-sky-300',             title: 'Customer authorized — deposit pending' },
  escalate_ap:     { label: 'escalate', cls: 'bg-amber-100 text-amber-900 border-amber-400',       title: 'Stale customer reply — escalate to AP' },
  dunner:          { label: 'dunner',   cls: 'bg-rose-100 text-rose-800 border-rose-300',          title: 'No signal — queue for dunning' },
  qb_sync_needed:  { label: 'qb sync',  cls: 'bg-zinc-100 text-zinc-700 border-zinc-300',          title: 'Missing quickbooks_invoice_id — sync needed' },
  investigate:     { label: 'investig.', cls: 'bg-purple-100 text-purple-800 border-purple-300',   title: 'Ambiguous bank match — disambiguate' },
};

function daysBetween(a: string, b: string): number {
  const ta = Date.parse(a); const tb = Date.parse(b);
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return 0;
  return Math.round((tb - ta) / (1000 * 60 * 60 * 24));
}

export default function InvoiceRow({ invoice, asOfDate, onClick, triage }: InvoiceRowProps) {
  const bucket = getBucketForInvoice(invoice.dueDate, asOfDate);
  const amount = invoice.balance;
  const label = invoice.projectName || invoice.memo || '';
  const daysOverdue = invoice.dueDate ? daysBetween(invoice.dueDate, asOfDate) : 0;
  const badge = triage ? ACTION_BADGE[triage.suggestedAction] : undefined;

  return (
    <TableRow
      className="hover:bg-muted/40 bg-muted/10"
    >
      {/* max-w-0 + overflow-hidden on the cell forces children with min-w-0
          to respect the column width set by the customer header rows above;
          without it, long descriptions grow the Name column and shift every
          bucket column to the right. */}
      <TableCell className="pl-10 text-sm max-w-0 overflow-hidden">
        <div className="flex items-center gap-2 min-w-0 overflow-hidden">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            aria-label={`View invoice ${invoice.docNumber}`}
            onClick={() => onClick(invoice)}
          >
            <span className="font-medium tabular-nums shrink-0">{invoice.docNumber}</span>
            <span className="text-[11px] tabular-nums text-muted-foreground shrink-0" title={`Invoice ${invoice.invoiceDate} → due ${invoice.dueDate}`}>
              {invoice.invoiceDate?.slice(0, 10) ?? '—'}
            </span>
          </button>
          {daysOverdue > 0 && (
            <span
              className={cn(
                'text-[11px] tabular-nums px-1 rounded shrink-0',
                daysOverdue >= 90 ? 'bg-red-100 text-destructive' :
                daysOverdue >= 60 ? 'bg-orange-100 text-orange-700' : // TODO GAP-FE-010: no orange semantic token
                daysOverdue >= 30 ? 'bg-yellow-100 text-warning' :
                'bg-zinc-100 text-zinc-600',
              )}
              title={`${daysOverdue} days overdue`}
            >
              +{daysOverdue}d
            </span>
          )}
          {badge && (
            <span
              className={cn('text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border shrink-0', badge.cls)}
              title={badge.title + (triage?.reason ? ` — ${triage.reason}` : '')}
            >
              {badge.label}
            </span>
          )}
          {triage?.bankMatch?.found && (
            <span
              className="text-[10px] px-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0"
              title={`RBC deposit $${triage.bankMatch.amount} on ${triage.bankMatch.depositDate} (${triage.bankMatch.status})`}
            >
              💰
            </span>
          )}
          {triage?.customerReply?.found && (
            <span
              className="text-[10px] px-1 rounded bg-sky-50 text-sky-700 border border-sky-200 shrink-0"
              title={`Reply ${triage.replyAgeDays}d ago from ${triage.customerReply.from}: ${(triage.customerReply.snippet ?? '').slice(0, 180)}`}
            >
              ✉
            </span>
          )}
          <span className="text-muted-foreground truncate min-w-0" title={label}>
            {label}
          </span>
        </div>
      </TableCell>
      {AGING_BUCKETS.map((b) => (
        <TableCell
          key={b}
          className={cn('text-right tabular-nums text-sm', BUCKET_CLASSNAMES[b])}
        >
          {b === bucket ? formatCurrency(amount) : ''}
        </TableCell>
      ))}
      <TableCell className="text-right tabular-nums text-sm">{formatCurrency(amount)}</TableCell>
    </TableRow>
  );
}
